import { get, reduce, map, join } from 'lodash';

export const getFieldName = object => {
  return object.purpose + object.id;
};

export const SubPurposes = {
  PhoneNumber: {
    AreaCode: 'phone_number-area_code',
    CityCode: 'phone_number-city_code',
    SubscriberNumber: 'phone_number-subscriber_number',
  },
  FullName: {
    LastName: 'full_name-last_name',
    FirstName: 'full_name-first_name',
    LastNamePhonetic: 'full_name-last_name_phonetic',
    FirstNamePhonetic: 'full_name-first_name_phonetic',
  },
  Address: {
    ZipcodeZone: 'address-zipcode_zone',
    ZipcodeArea: 'address-zipcode_area',
    Prefecture: 'address-prefecture',
    City: 'address-city',
    Street: 'address-street',
    Extended: 'address-extended',
  }
};

export const inspect = (object) => {
  const purpose = get(object, 'purpose');
  const dataMode = get(object, 'data.mode');
  const dataStyle = get(object, 'data.style');
  const dataPhonetic = get(object, 'data.enable_phonetic_input', false);

  return {
    multipleSelection: ['multiple_choice', 'single_choice', 'selectbox'].includes(purpose) && dataMode === 'selection',
    multipleToggle: ['multiple_choice', 'single_choice', 'selectbox'].includes(purpose) && dataMode === 'toggle',
    phoneNumberDefault: purpose === 'phone_number' && dataStyle === 'default',
    phoneNumberSeparate: purpose === 'phone_number' && dataStyle === 'separate',
    fullName: purpose === 'full_name' && !dataPhonetic,
    fullNamePhonetic: purpose === 'full_name' && dataPhonetic,
    address: purpose === 'address',
  };
};

// Mostly for splitting formObject with multiple fields into smaller form objects
export const convertFormObject = (object) => {
  const inspected = inspect(object);

  if (inspected.phoneNumberSeparate) {
    const areaCode = {
      ...object,
      id: object.data.area_code.id,
      data: { ...object.data, ...object.data.area_code },
      purpose: SubPurposes.PhoneNumber.AreaCode,
    };
    const cityCode = {
      ...object,
      id: object.data.city_code.id,
      data: { ...object.data, ...object.data.city_code },
      purpose: SubPurposes.PhoneNumber.CityCode,
    };
    const subscriberNumber = {
      ...object,
      id: object.data.subscriber_number.id,
      data: { ...object.data, ...object.data.subscriber_number },
      purpose: SubPurposes.PhoneNumber.SubscriberNumber,
    };

    return {
      areaCode,
      cityCode,
      subscriberNumber,
    };
  } else if (inspected.fullName || inspected.fullNamePhonetic) {
    const lastName = {
      ...object,
      id: object.data.last_name.id,
      data: { ...object.data, ...object.data.last_name },
      purpose: SubPurposes.FullName.LastName,
    };
    const firstName = {
      ...object,
      id: object.data.first_name.id,
      data: { ...object.data, ...object.data.first_name },
      purpose: SubPurposes.FullName.FirstName,
    };

    if (inspected.fullName) {
      return { lastName, firstName };
    }

    const lastNamePhonetic = {
      ...object,
      id: object.data.phonetic.last_name.id,
      data: { ...object.data, ...object.data.phonetic.last_name },
      purpose: SubPurposes.FullName.LastNamePhonetic,
    };
    const firstNamePhonetic = {
      ...object,
      id: object.data.phonetic.first_name.id,
      data: { ...object.data, ...object.data.phonetic.first_name },
      purpose: SubPurposes.FullName.FirstNamePhonetic,
    };

    return { lastName, firstName, lastNamePhonetic, firstNamePhonetic };
  } else if (inspected.address) {
    const zipcodeZone = {
      ...object,
      ...object.data.zipcode,
      ...object.data.zipcode.zone,
      data: { ...object.data, ...object.data.zipcode.zone },
      purpose: SubPurposes.Address.ZipcodeZone,
    };
    const zipcodeArea = {
      ...object,
      ...object.data.zipcode,
      ...object.data.zipcode.area,
      data: { ...object.data, ...object.data.zipcode.area },
      purpose: SubPurposes.Address.ZipcodeArea,
    };
    const prefecture = {
      ...object,
      ...object.data.prefecture,
      data: { ...object.data, ...object.data.prefecture },
      purpose: SubPurposes.Address.Prefecture,
    };
    const city = {
      ...object,
      ...object.data.city,
      data: { ...object.data, ...object.data.city },
      purpose: SubPurposes.Address.City,
    };
    const street = {
      ...object,
      ...object.data.street,
      data: { ...object.data, ...object.data.street },
      purpose: SubPurposes.Address.Street,
    };
    const extended = object.data.extended && {
      ...object,
      ...object.data.extended,
      data: { ...object.data, ...object.data.extended },
      purpose: SubPurposes.Address.Extended,
    };

    return { zipcodeZone, zipcodeArea, prefecture, city, street, extended };
  }

  return object;
};

export const convertFormObjects = (objects) => reduce(objects, (objs, obj) => {
  const inspected = inspect(obj);

  if (inspected.phoneNumberSeparate) {
    const { areaCode, cityCode, subscriberNumber } = convertFormObject(obj);
    objs.push(areaCode, cityCode, subscriberNumber);
  } else if (inspected.fullName) {
    const c = convertFormObject(obj);
    objs.push(c.lastName, c.firstName);
  } else if (inspected.fullNamePhonetic) {
    const c = convertFormObject(obj);
    objs.push(c.lastName, c.firstName, c.lastNamePhonetic, c.firstNamePhonetic);
  } else if (inspected.address) {
    const c = convertFormObject(obj);
    objs.push(c.zipcodeZone, c.zipcodeArea, c.prefecture, c.city, c.street);
    if (c.extended) objs.push(c.extended);
  } else {
    objs.push(obj);
  }

  return objs;
}, []);

export const getInitialValues = objects => {
  return reduce(objects, (accum, object) => {
    const inspected = inspect(object);
    const setValue = (obj) => {
      const name = getFieldName(obj);
      accum[name] = getInitialValue(obj) || getDefaultValue(obj);
    };

    if (inspected.phoneNumberSeparate) {
      const { areaCode, cityCode, subscriberNumber } = convertFormObject(object);
      setValue(areaCode);
      setValue(cityCode);
      setValue(subscriberNumber);
    } else if (inspected.fullName) {
      const c = convertFormObject(object);
      setValue(c.firstName);
      setValue(c.lastName);
    } else if (inspected.fullNamePhonetic) {
      const c = convertFormObject(object);
      setValue(c.firstName);
      setValue(c.lastName);
      setValue(c.firstNamePhonetic);
      setValue(c.lastNamePhonetic);
    } else if (inspected.address) {
      const c = convertFormObject(object);
      setValue(c.zipcodeZone);
      setValue(c.zipcodeArea);
      setValue(c.prefecture);
      setValue(c.city);
      setValue(c.street);
      if (c.extended) setValue(c.extended);
    } else {
      setValue(object);
    }

    return accum;
  }, {});
};

const getInitialValue = (object) => {
  const { initial_value } = object.data;
  if (!initial_value) {
    return initial_value;
  }

  const inspected = inspect(object);
  if (inspected.multipleSelection) {
    // put the string value in array
    return initial_value.split(',');
  } else {
    return initial_value;
  }
};

const getDefaultValue = (object) => {
  const inspected = inspect(object);
  if (inspected.multipleSelection) {
    return [];
  }

  return '';
};

export const getSubmitObjects = (formData, data) => {
  const objects = convertFormObjects(formData.objects);
  return map(objects, object => {
    const { id, purpose, label } = object;
    const name = getFieldName(object);
    const value = data[name];
    return {
      id, purpose, label,
      value: {
        text: getSubmitValue(object, value)
      }
    };
  });
};

const getSubmitValue = (object, value) => {
  const inspected = inspect(object);

  if (inspected.multipleSelection) {
    // multiple_choise selection is expected to be returned with string concatenated by comma
    // https://www.pivotaltracker.com/story/show/172988217
    return join(value, ',');
  }

  return value;
};
